"use strict";
/**
 * @fileoverview Nexus插件基类 - 提供插件开发的核心功能
 * @description 该文件定义了Nexus框架的插件基类，包含插件生命周期管理、
 * 错误处理、依赖管理等核心功能。所有用户插件都应继承此基类。
 *
 * @author ESEngine Team
 * @version 2.0.0
 * @since 1.0.0
 * @license MIT
 *
 * @example
 * ```typescript
 * @Plugin({
 *   name: 'my-plugin',
 *   version: '1.0.0',
 *   description: '我的插件'
 * })
 * class MyPlugin extends NexusPlugin {
 *   public onLoad(): void {
 *     console.log('插件加载');
 *   }
 *
 *   public onUnload(): void {
 *     console.log('插件卸载');
 *   }
 * }
 * ```
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.NexusPlugin = void 0;
exports.Plugin = Plugin;
exports.getPluginMetadata = getPluginMetadata;
exports.createPlugin = createPlugin;
const Types_1 = require("../Types");
const NexusExtension_1 = require("./NexusExtension");
const NexusThemeManager_1 = require("./NexusThemeManager");
const NexusErrorManager_1 = require("./NexusErrorManager");
const NexusDependencyManager_1 = require("./NexusDependencyManager");
/**
 * Nexus插件基类
 *
 * @description 提供插件开发的核心功能，包括：
 * - 插件生命周期管理
 * - 错误处理和插件沙盒
 * - 主题系统集成
 * - 依赖管理
 * - 自动注册和安装
 *
 * @public
 * @abstract
 * @since 1.0.0
 * @version 2.0.0
 *
 * @example
 * ```typescript
 * @Plugin({
 *   name: 'example-plugin',
 *   version: '1.0.0'
 * })
 * class ExamplePlugin extends NexusPlugin {
 *   public onLoad(): void {
 *     console.log('插件加载完成');
 *   }
 *
 *   public onUnload(): void {
 *     console.log('插件卸载');
 *   }
 * }
 * ```
 *
 * @see {@link NexusPluginConfig} 插件配置接口
 * @see {@link NexusExtension} 扩展管理器
 */
class NexusPlugin {
    /**
     * 构造函数
     *
     * @description 初始化插件实例，创建沙盒环境并注册依赖
     * @param config 插件配置对象
     *
     * @since 1.0.0
     * @version 2.0.0
     */
    constructor(config) {
        var _a, _b, _c, _d;
        /**
         * 主题管理器实例
         *
         * @description 用于管理插件的主题样式，支持动态主题切换
         * @protected
         * @readonly
         * @since 1.0.0
         */
        this.themeManager = NexusThemeManager_1.ThemeManager;
        /**
         * 错误管理器实例
         *
         * @description 用于处理插件运行过程中的错误，提供错误隔离和恢复机制
         * @protected
         * @readonly
         * @since 1.0.0
         */
        this.errorManager = NexusErrorManager_1.ErrorManager;
        /**
         * 依赖管理器实例
         *
         * @description 用于管理插件间的依赖关系，确保正确的加载顺序
         * @protected
         * @readonly
         * @since 1.0.0
         */
        this.dependencyManager = NexusDependencyManager_1.DependencyManager;
        /**
         * 插件初始化状态
         *
         * @description 标识插件是否已经初始化完成
         * @protected
         * @since 1.0.0
         */
        this.isInitialized = false;
        this.config = config;
        // 创建插件沙盒
        this.sandbox = this.errorManager.createPluginSandbox(config.name, (_b = (_a = config.errorHandling) === null || _a === void 0 ? void 0 : _a.isolate) !== null && _b !== void 0 ? _b : true, (_d = (_c = config.errorHandling) === null || _c === void 0 ? void 0 : _c.maxRetries) !== null && _d !== void 0 ? _d : 3);
        // 如果插件有主题配置，注册主题
        if (config.theme) {
            this.initializeTheme();
        }
        // 注册依赖
        this.registerDependencies();
    }
    /**
     * 初始化主题
     *
     * @description 根据插件配置初始化主题，支持字符串主题名称或自定义主题对象
     * @protected
     * @since 2.0.0
     */
    initializeTheme() {
        if (typeof this.config.theme === 'string') {
            this.themeManager.setTheme(this.config.theme);
        }
        else if (this.config.theme) {
            this.themeManager.registerTheme(this.config.name, this.config.theme);
            this.themeManager.setTheme(this.config.name);
        }
    }
    /**
     * 注册依赖
     *
     * @description 将插件及其依赖关系注册到依赖管理器中
     * @protected
     * @since 2.0.0
     */
    registerDependencies() {
        // 注册插件本身
        this.dependencyManager.registerDependency(this.config.name, this.config.version, this);
        // 注册插件的依赖图
        if (this.config.dependencies && this.config.dependencies.length > 0) {
            const deps = this.config.dependencies.map(dep => ({
                name: dep,
                version: '*', // 可以在子类中重写更具体的版本要求
                required: true
            }));
            this.dependencyManager.registerDependencyGraph(this.config.name, deps);
        }
    }
    /**
     * 获取插件配置
     *
     * @description 返回插件的配置对象，包含插件的元数据和设置
     * @returns 插件配置对象
     * @public
     * @since 1.0.0
     */
    getConfig() {
        return this.config;
    }
    /**
     * 获取插件名称
     *
     * @description 返回插件的唯一标识符
     * @returns 插件名称字符串
     * @public
     * @since 1.0.0
     */
    getName() {
        return this.config.name;
    }
    /**
     * 获取插件版本
     *
     * @description 返回插件的版本号，遵循语义化版本规范
     * @returns 插件版本字符串
     * @public
     * @since 1.0.0
     */
    getVersion() {
        return this.config.version;
    }
    /**
     * 安全加载插件
     *
     * @description 在错误隔离的沙盒环境中加载插件，确保插件错误不会影响框架稳定性。
     * 如果插件加载失败，会记录错误信息并尝试恢复。
     *
     * @public
     * @returns {boolean} 加载成功返回true，失败返回false
     *
     * @throws {NexusError} 当插件配置无效时抛出
     *
     * @example
     * ```typescript
     * const plugin = new MyPlugin(config);
     * if (plugin.safeLoad()) {
     *   console.log('插件加载成功');
     * } else {
     *   console.error('插件加载失败');
     * }
     * ```
     *
     * @since 2.0.0
     * @version 2.0.0
     */
    safeLoad() {
        return this.sandbox.execute(() => {
            this.onLoad();
            this.isInitialized = true;
            return true;
        }, { methodName: 'onLoad' }) !== null;
    }
    /**
     * 安全卸载插件
     *
     * @description 在错误隔离的沙盒环境中卸载插件，确保卸载过程不会影响其他插件。
     * 如果卸载失败，会记录错误信息并尝试强制清理。
     *
     * @public
     * @returns {boolean} 卸载成功返回true，失败返回false
     *
     * @example
     * ```typescript
     * if (plugin.safeUnload()) {
     *   console.log('插件卸载成功');
     * } else {
     *   console.error('插件卸载失败');
     * }
     * ```
     *
     * @since 2.0.0
     * @version 2.0.0
     */
    safeUnload() {
        return this.sandbox.execute(() => {
            this.onUnload();
            this.isInitialized = false;
            return true;
        }, { methodName: 'onUnload' }) !== null;
    }
    /**
     * 获取插件状态
     *
     * @description 返回插件的详细状态信息，包括初始化状态、沙盒状态、主题状态和依赖状态
     * @returns 插件状态对象
     * @public
     * @since 2.0.0
     *
     * @example
     * ```typescript
     * const status = plugin.getStatus();
     * console.log(`插件 ${status.name} 状态:`, status);
     * ```
     */
    getStatus() {
        const dependencyResult = this.dependencyManager.resolveDependencies(this.config.name);
        return {
            name: this.config.name,
            version: this.config.version,
            initialized: this.isInitialized,
            sandboxStatus: this.sandbox.getStatus(),
            themeApplied: !!this.config.theme,
            dependenciesResolved: dependencyResult.missing.length === 0 &&
                dependencyResult.incompatible.length === 0 &&
                dependencyResult.circular.length === 0
        };
    }
    /**
     * 自动注册插件到Nexus系统
     *
     * @description 将插件实例自动注册到Nexus扩展系统中，无需手动调用。
     * 这是框架的核心功能之一，实现了插件的自动发现和注册机制。
     *
     * @public
     * @since 2.0.0
     *
     * @example
     * ```typescript
     * const plugin = new MyPlugin(config);
     * plugin.autoRegister(); // 自动注册到系统
     * ```
     */
    autoRegister() {
        this.sandbox.execute(() => {
            NexusExtension_1.NexusExtension.Instance.registerPlugin(this);
        }, { methodName: 'autoRegister' });
    }
    /**
     * 自动生成并安装到Cocos Creator
     *
     * @description 一键生成所有必要的文件和配置，包括main.ts、package.json、面板文件等。
     * 这是框架的终极功能，实现了从插件代码到Cocos Creator扩展的完全自动化。
     *
     * @public
     * @returns 安装结果对象，包含成功状态、生成的文件列表和消息
     * @since 2.0.0
     *
     * @example
     * ```typescript
     * const plugin = new MyPlugin(config);
     * const result = plugin.autoInstall();
     * if (result.success) {
     *   console.log('插件安装成功');
     *   console.log('生成的文件:', result.files);
     * } else {
     *   console.error('插件安装失败:', result.message);
     * }
     * ```
     */
    autoInstall() {
        try {
            const result = NexusExtension_1.NexusExtension.Instance.integratePlugin(this);
            return {
                success: true,
                files: {
                    'main.ts': result.mainTs,
                    'package.json': JSON.stringify(result.packageJson, null, 2),
                    ...Object.fromEntries(Array.from(result.panelFiles.entries()).map(([name, content]) => [`${name}.ts`, content]))
                },
                message: `插件 ${this.getName()} 已成功生成所有必要文件`
            };
        }
        catch (error) {
            return {
                success: false,
                files: {},
                message: `插件安装失败: ${error}`
            };
        }
    }
}
exports.NexusPlugin = NexusPlugin;
/**
 * 插件装饰器
 *
 * @description 用于标记类为Nexus插件的装饰器，将插件配置信息附加到类的元数据中。
 * 框架会在运行时读取这些元数据来管理插件的生命周期。
 *
 * @param config 插件配置对象
 * @returns 装饰器函数
 *
 * @public
 * @decorator
 * @since 1.0.0
 *
 * @example
 * ```typescript
 * @Plugin({
 *   name: 'my-plugin',
 *   version: '1.0.0',
 *   description: '我的第一个插件'
 * })
 * class MyPlugin extends NexusPlugin {
 *   public onLoad(): void {
 *     console.log('插件加载完成');
 *   }
 *
 *   public onUnload(): void {
 *     console.log('插件卸载');
 *   }
 * }
 * ```
 */
function Plugin(config) {
    return function (constructor) {
        Reflect.defineMetadata(Types_1.NEXUS_PLUGIN_METADATA, config, constructor);
        return constructor;
    };
}
/**
 * 获取插件元数据
 *
 * @description 从类的元数据中获取插件配置信息，用于框架内部管理插件
 * @param target 目标类或实例
 * @returns 插件配置对象，如果不存在则返回undefined
 *
 * @public
 * @since 1.0.0
 *
 * @example
 * ```typescript
 * const metadata = getPluginMetadata(MyPlugin);
 * if (metadata) {
 *   console.log('插件名称:', metadata.name);
 *   console.log('插件版本:', metadata.version);
 * }
 * ```
 */
function getPluginMetadata(target) {
    return Reflect.getMetadata(Types_1.NEXUS_PLUGIN_METADATA, target);
}
/**
 * 创建插件实例的工厂函数
 *
 * @description 通过配置对象快速创建插件实例，适用于简单的插件开发场景。
 * 对于复杂的插件，建议继承NexusPlugin类并使用@Plugin装饰器。
 *
 * @param options 插件配置选项
 * @returns 插件实例
 *
 * @public
 * @since 1.0.0
 *
 * @example
 * ```typescript
 * const plugin = createPlugin({
 *   name: 'simple-plugin',
 *   version: '1.0.0',
 *   description: '简单插件示例',
 *   onLoad: () => {
 *     console.log('插件加载');
 *   },
 *   onUnload: () => {
 *     console.log('插件卸载');
 *   }
 * });
 * ```
 */
function createPlugin(options) {
    var _a;
    let GeneratedPlugin = class GeneratedPlugin extends NexusPlugin {
        onLoad() {
            console.log(`插件 ${options.name} 加载成功`);
            if (options.onLoad) {
                options.onLoad();
            }
        }
        onUnload() {
            console.log(`插件 ${options.name} 卸载`);
            if (options.onUnload) {
                options.onUnload();
            }
        }
    };
    GeneratedPlugin = __decorate([
        Plugin({
            name: options.name,
            version: options.version || '1.0.0',
            description: options.description || `${options.name} 插件`,
            author: options.author || 'Unknown',
            panels: options.panels || {},
            menu: ((_a = options.menu) === null || _a === void 0 ? void 0 : _a.map(m => ({
                path: m.path || `扩展/${options.name}`,
                label: m.label,
                action: m.action
            }))) || []
        })
    ], GeneratedPlugin);
    return new GeneratedPlugin({
        name: options.name,
        version: options.version || '1.0.0',
        description: options.description || `${options.name} 插件`,
        author: options.author || 'Unknown'
    });
}
